/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useCallback, useRef } from 'react'
import * as Yup from 'yup'
import { Form } from '@unform/web'
import { FormHandles } from '@unform/core'
import { useHistory } from 'react-router-dom'

import { FiCheck, FiCalendar } from 'react-icons/fi'
import { FaUser } from 'react-icons/fa'
import usePersistedState from '../../hooks/usePersistedState'

import Button from '../../components/Button'
import Header from '../../components/Header'
import InputSelect from '../../components/InputSelect'
import Input from '../../components/Input'

import { UserDetails } from '../../utils/interfaces'
import {
  Container,
  Content,
  RadioButton,
  BtnVoltar,
  BtnContato,
} from './styles'
import getValidationErrors from '../../utils/getValidationErrors'
import moment from 'moment'

interface FormData {
  dtInicioPPE: string;
  dtFimPPE: string;
  grauParentescoPPE: string;
  cargoPPE: string;
}

const FatcaInfo: React.FC = () => {
  const [userDetails, setUserDetails] = usePersistedState<UserDetails>(
    'userDetails',
    {} as UserDetails,
  )

  const [ppe, setPPE] = useState(userDetails.ppe ? userDetails.ppe : 'N')
  const [usperson, setUsperson] = useState('N')
  const [grauParentescoPPE, setGrauParentescoPPE] = useState({
    label: userDetails.dcrGrauParentescoPPE ? userDetails.dcrGrauParentescoPPE : '',
    value: userDetails.grauParentescoPPE ? userDetails.grauParentescoPPE : ''
  })

  const options = [
    { label: 'Próprio(a) participante', value: 'Proprio(a) participante' },
    { label: 'Parente até 1° grau: pai, mãe ou filho(a)', value: 'Parente ate 1 grau: pai, mae ou filho(a)' },
    { label: 'Cônjuge ou companheiro(a)', value: 'Conjuge ou companheiro(a)' },
    { label: 'Enteado(a)', value: 'Enteado(a)' },
    { label: 'Representante ou procurador(a)', value: 'Representante ou procurador(a)' },
  ]

  const history = useHistory()
  const formRef = useRef<FormHandles>(null)

  const handleChangeGrauParentesco = useCallback((option: any) => {
    setGrauParentescoPPE(option)
  }, [])

  const handleClick = useCallback(() => {
    if (ppe === 'S' || ppe === 'R') {
      formRef.current?.submitForm()
    } else {
      handleContinue()
    }
  }, [ppe])

  const handleContinue = async () => {
    await setUserDetails({
      ...userDetails,
      ppe,
      usperson,
      grauParentescoPPE: '',
      dcrGrauParentescoPPE: '',
      cargoPPE: '',
      dtIniVigPPE: '',
      dtFimVigPPE: '',
    })

    history.push('/confirm-ownership')
  }

  const handleSubmit = async (data: FormData) => {

    try {
      formRef.current?.setErrors({})
      const schema = Yup.object().shape({
        grauParentescoPPE: Yup.string().required('Grau de parentesco é obrigatório.'),
        cargoPPE: Yup.string()
          .required('Cargo é obrigatório.')
          .min(3, 'Cargo é obrigatório.'),
        dtInicioPPE: Yup.string()
          .required('Data de início é obrigatória.')
          .test('',
            'Data de início inválida.',
            () => {
              if (data.dtInicioPPE.length === 10) {
                return moment(
                  data.dtInicioPPE.split('/').reverse().join('-')
                ).isValid()
              } else {
                return false
              }
            }),
        dtFimPPE: Yup.string()
          .test('datafim',
            'Data de término inválida.',
            () => {
              if (data.dtFimPPE.length === 10) {
                return moment(
                  data.dtFimPPE.split('/').reverse().join('-')
                ).isValid()
              } else if (data.dtFimPPE.length > 0 && data.dtFimPPE.length < 10) {
                return false
              } else {
                return true
              }
            }),
      })

      await schema.validate(data, { abortEarly: false })

      const dtIniPPE = data?.dtInicioPPE.split('/').reverse().join('-')
      const dtFimPPE = data?.dtFimPPE.split('/').reverse().join('-')

      await setUserDetails({
        ...userDetails,
        usperson,
        ppe: grauParentescoPPE.value === 'Proprio(a) participante' ? 'S' : 'R',
        grauParentescoPPE: grauParentescoPPE.value,
        dcrGrauParentescoPPE: grauParentescoPPE.label,
        cargoPPE: data.cargoPPE,
        dtIniVigPPE: dtIniPPE,
        dtFimVigPPE: dtFimPPE,
      })

      // history.push('/confirm-ownership')
      history.push('/taxation')
    } catch (err) {
      formRef.current?.setErrors(getValidationErrors(err))
    }

  }

  return (
    <>
      <Header />
      <Container>
        <Content>
          <strong>Pessoa politicamente exposta (PPE):</strong>
          <span>
            São pessoas expostas politicamente (PEP) aqueles eu atualmente ou
            nos últimos 5 anos:
          </span>

          <div>
            <ul>
              <li>
                Detentores de mandatos eletivos dos Poderes Executivo e
                Legislativo da União;
              </li>
              <li>
                Ocupantes de cargo no Poder Executivo da União como ministro de
                Estado ou equiparado, de natureza especial ou equivalente;
              </li>
              <li>
                Presidente, vice-presidente e diretor, ou equivalentes de
                entidades da administração pública indireta;
              </li>
              <li>
                Exercentes de função pública federal com Direção e
                Assessoramento Superiores (DAS), nível 6, ou equivalente;
              </li>
              <li>
                Membros do CNJ, STF, STJ e outros tribunais superiores, TRF,
                TRT, TRE, CSJT e CJF;
              </li>
              <li>Membros do Conselho Nacional do MP;</li>
              <li>Procurador-Geral ou Vice-Procurador-Geral da República;</li>
              <li>Procurador-Geral do Trabalho e/ou da Justiça Militar;</li>
              <li>Subprocuradores-Gerais da República</li>
              <li>
                Procuradores-Gerais de Justiça dos Estados e do Distrito
                Federal;
              </li>
              <li>
                Membros do TCU, Procurador-Geral e/ou Subprocuradores-Gerais do
                MP junto ao TCU;
              </li>
              <li>
                Presidentes e os tesoureiros nacionais ou equivalentes, de
                partidos políticos;
              </li>
              <li>
                Governadores e os secretários de Estados e do Distrito Federal;
              </li>
              <li>Deputados estaduais e distritais;</li>
              <li>
                Presidentes ou equivalentes de entidades da administração
                pública indireta estadual e distrital;
              </li>
              <li>
                Presidentes de Tribunais de Justiça, Tribunais Militares,
                Tribunais de Contas ou equivalentes dos Estados e do Distrito
                Federal;
              </li>
              <li>Prefeitos, os vereadores, os secretários municipais;</li>
              <li>
                Presidentes ou equivalentes de entidades da administração
                pública indireta municipal;
              </li>
              <li>
                Presidentes de Tribunais de Contas ou equivalentes dos
                municípios;
              </li>
              <li>
                Pessoas que, no exterior, sejam chefes de estado ou de governo,
                políticos de escalões superiores, ocupantes de cargos
                governamentais de escalões superiores, oficiais-generais e
                membros de escalões superiores do Poder Judiciário, executivos
                de escalões superiores de empresas públicas ou dirigentes de
                partidos políticos;
              </li>
              <li>
                Dirigentes de escalões superiores de entidades de direito
                internacional público ou privado.
              </li>
            </ul>
          </div>

          <span>
            Marque se tiver relação próxima, como representantes ou familiar* de
            PEP.
          </span>
          <small>
            *Familiar = cônjuge, companheiro(a), enteado(a), pai, mãe, filho,
            avô/avó, neto(a).
          </small>

          <RadioButton>
            <div>
              <BtnContato
                type="button"
                isActive={ppe === 'S' || ppe === 'R'}
                onClick={() => setPPE('S')}
              >
                Sim
              </BtnContato>
              <BtnContato
                type="button"
                isActive={ppe === 'N'}
                onClick={() => setPPE('N')}
              >
                Não
              </BtnContato>
            </div>
          </RadioButton>

          {ppe === 'S' || ppe === 'R'
            ? (
              <Form
                ref={formRef}
                onSubmit={handleSubmit}
                initialData={{
                  grauParentescoPPE: grauParentescoPPE.value,
                  dtInicioPPE: userDetails.dtIniVigPPE,
                  dtFimPPE: userDetails.dtFimVigPPE,
                  cargoPPE: userDetails.cargoPPE,
                }}>
                <InputSelect
                  name="grauParentescoPPE"
                  options={options}
                  value={grauParentescoPPE}
                  onChange={e => handleChangeGrauParentesco(e)}
                  placeholder="Informe o grau de parentesco"
                />
                <Input name="cargoPPE" placeholder="Cargo" icon={FaUser} />
                <Input
                  icon={FiCalendar}
                  name="dtInicioPPE"
                  mask="date"
                  placeholder="Data de início"
                />
                <Input
                  icon={FiCalendar}
                  name="dtFimPPE"
                  mask="date"
                  placeholder="Data de término"
                />
              </Form>
            ) : null}
        </Content>

        <Content>
          <span>É residente no exterior para fins fiscais?</span>

          <RadioButton>
            <div>
              <BtnContato
                type="button"
                isActive={usperson === 'S'}
                onClick={() => setUsperson('S')}
              >
                Sim
              </BtnContato>
              <BtnContato
                type="button"
                isActive={usperson === 'N'}
                onClick={() => setUsperson('N')}
              >
                Não
              </BtnContato>
            </div>
          </RadioButton>
        </Content>

        <Button
          type="button"
          fontSize="normal"
          color="green"
          onClick={handleClick}
        >
          <FiCheck size={45} />
          Continuar
        </Button>
        <BtnVoltar type="button" onClick={() => history.push('/beneficiary')}>
          &lt; Anterior
        </BtnVoltar>
      </Container>
    </>
  )
}

export default FatcaInfo
